import { useEffect } from 'react'
import { Link, useNavigate, useParams } from 'react-router-dom'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { ArrowLeft, Star, Trash2, Download, CornerUpLeft, Forward as ForwardIcon, Mail } from 'lucide-react'

import { deleteMessage, getMessage, markMessage } from '../lib/api'

export default function MessageRead() {
  const { mailbox = '', messageId = '' } = useParams()
  const navigate = useNavigate()
  const qc = useQueryClient()

  const { data, isLoading, error } = useQuery({
    queryKey: ['message', mailbox, messageId],
    queryFn: () => getMessage(messageId, mailbox),
  })

  const mark = useMutation({
    mutationFn: (patch: { read?: boolean; starred?: boolean }) => markMessage(messageId, mailbox, patch),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['messages', mailbox] }),
  })

  const del = useMutation({
    mutationFn: () => deleteMessage(messageId, mailbox),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['messages', mailbox] })
      navigate(`/inbox/${encodeURIComponent(mailbox)}`)
    },
  })

  useEffect(() => {
    if (data && !data.read) mark.mutate({ read: true })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data?.message_id])

  if (isLoading) return <div className="p-6 text-ink-500">Loading…</div>
  if (error) return <div className="p-6 text-red-700">Error: {error instanceof Error ? error.message : String(error)}</div>
  if (!data) return null

  return (
    <div className="h-full flex flex-col">
      <header className="border-b border-line px-6 py-4 flex items-center gap-2 flex-wrap">
        <Link to={`/inbox/${encodeURIComponent(mailbox)}`} className="pill-ghost">
          <ArrowLeft size={16} /> Inbox
        </Link>
        <div className="w-2" />
        <Link to={`/compose?mode=reply&original_id=${encodeURIComponent(messageId)}&mailbox=${encodeURIComponent(mailbox)}&in_reply_to=${encodeURIComponent(messageId)}${data.delivered_to ? `&from=${encodeURIComponent(data.delivered_to)}` : ''}`}
              className="pill-ghost"><CornerUpLeft size={16} /> Reply</Link>
        <Link to={`/compose?mode=reply-all&original_id=${encodeURIComponent(messageId)}&mailbox=${encodeURIComponent(mailbox)}&in_reply_to=${encodeURIComponent(messageId)}${data.delivered_to ? `&from=${encodeURIComponent(data.delivered_to)}` : ''}`}
              className="pill-ghost"><CornerUpLeft size={16} /> Reply all</Link>
        <Link to={`/compose?mode=forward&original_id=${encodeURIComponent(messageId)}&mailbox=${encodeURIComponent(mailbox)}${data.delivered_to ? `&from=${encodeURIComponent(data.delivered_to)}` : ''}`}
              className="pill-ghost"><ForwardIcon size={16} /> Forward</Link>
        <div className="flex-1" />
        <button
          onClick={() => mark.mutate({ read: false })}
          className="pill-ghost"
          title="Mark as unread and return to inbox"
        >
          <Mail size={16} /> Mark unread
        </button>
        <button
          onClick={() => mark.mutate({ starred: !data.starred })}
          className={`pill-ghost ${data.starred ? 'text-pulse' : ''}`}
        >
          <Star size={16} className={data.starred ? 'fill-pulse' : ''} />
          {data.starred ? 'Starred' : 'Star'}
        </button>
        <button onClick={() => del.mutate()} className="pill-danger">
          <Trash2 size={16} /> Delete
        </button>
      </header>

      <div className="p-6 border-b border-line">
        <div className="text-xl font-semibold text-ink-900 tracking-tight mb-3">
          {data.subject || <span className="text-ink-300">(no subject)</span>}
        </div>
        <div className="text-sm text-ink-700 space-y-0.5">
          <div><span className="text-ink-300 uppercase text-[10px] tracking-widest mr-2">From</span>{data.sender}</div>
          <div><span className="text-ink-300 uppercase text-[10px] tracking-widest mr-2">To</span>{data.recipient}</div>
          {data.delivered_to && data.delivered_to.toLowerCase() !== data.recipient.toLowerCase() && (
            <div><span className="text-ink-300 uppercase text-[10px] tracking-widest mr-2">Via</span>{data.delivered_to}</div>
          )}
          <div><span className="text-ink-300 uppercase text-[10px] tracking-widest mr-2">Received</span>{data.received_at}</div>
        </div>
      </div>

      <div className="p-6 overflow-auto flex-1">
        {data.body_html ? (
          <iframe
            title="message body"
            srcDoc={data.body_html}
            sandbox="allow-popups allow-popups-to-escape-sandbox"
            className="w-full h-[70vh] card-inset"
          />
        ) : (
          <pre className="whitespace-pre-wrap font-sans text-ink-900 text-sm leading-relaxed">{data.body_text}</pre>
        )}

        {data.attachments?.length > 0 && (
          <div className="mt-6">
            <div className="h-eyebrow mb-2">Attachments</div>
            <ul className="space-y-2">
              {data.attachments.map((a, i) => (
                <li key={i} className="card-inset p-3 flex items-center gap-3 text-sm">
                  <span className="font-mono text-ink-900 flex-1 truncate">{a.filename}</span>
                  <span className="text-ink-500 text-xs">{a.content_type} · {Math.round(a.size_bytes / 1024)} KB</span>
                  {a.content_b64 && (
                    <a
                      href={`data:${a.content_type};base64,${a.content_b64}`}
                      download={a.filename}
                      className="pill-ghost"
                    >
                      <Download size={14} /> Download
                    </a>
                  )}
                </li>
              ))}
            </ul>
          </div>
        )}
      </div>
    </div>
  )
}
